import useAutocomplete, { AutocompleteInputChangeReason } from '@material-ui/lab/useAutocomplete';
import { clsx } from 'clsx';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DayPickerProps } from 'react-day-picker';
import { useDispatch, useSelector } from 'react-redux';

import { EPage, PAGE_PATHNAME } from '@/constants/pages';
import { ESearchFilters } from '@/constants/searchFilters';
import { useAuthModalState } from '@/hooks/useAuthModalState';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import useComponentVisible from '@/hooks/useComponentVisible';
import { useIsLocalityPage } from '@/hooks/useIsLocalityPage';
import {
  ILocationSearchAutocompleteListOption,
  useLocationSearchAutocompleteOptions,
} from '@/hooks/useLocationSearchAutocompleteOptions';
import { useLocationSearchSuggestions } from '@/hooks/useLocationSearchSuggestions';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import { IHeaderSearchFilterDates, IHeaderSearchFilterGuests } from '@/redux/modules/globalHeader';
import { applySearchFilter } from '@/redux/modules/search';
import { getSearchResults } from '@/redux/selectors/search/searchResults';
import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';
import { ERentalCategory } from '@/services/types/search/rentals/id';
import { IGroupOption, limitRecentSearches } from '@/utility/autoCompleteGroupedOptions';
import { formatDateFullRange } from '@/utility/format-date';
import { stripDiacritics } from '@/utility/stripDiacritics';
import {
  LocationGroupType,
  mapLocationLabelToGroupName,
  setSurfacedLocation,
} from '@/utility/surfacedLocation';

import { QUERYSTRING_DATE_FORMAT } from '../../SearchHeader';
import { EVENT_OPEN_SEARCH_DATES } from '../events';
import DateContent from './DateContent/DateContent';
import DetailsContent from './DetailsContent/DetailsContent';
import GuestsContent from './GuestsContent/GuestsContent';
import LocationSearchContent from './LocationSearchContent';
import LocationSearchInput from './LocationSearchInput';
import ModeSwitcherContent from './ModeSwitcherContent';
import { SearchModesPopover } from './SearchModesPopover';
import { DateTab, GuestsTab, ModeSwitcher, SearchButton } from './SearchTabs';

enum SEARCH_TAB {
  DATES = 'DATES',
  DETAILS = 'DETAILS',
  LOCATION = 'LOCATION',
  MODE = 'MODE',
}

interface IDesktopNavbarSearchProps {
  handleRecentSearchNavigation?: (url: string) => void;
  onSubmit?: () => void;
  location?: { lat: number; lng: number };
  address?: string;
  guests?: IHeaderSearchFilterGuests;
  dates?: IHeaderSearchFilterDates;
  recentSearches?: ILocationSearchAutocompleteListOption[];
  maxRecentSearches: number;
  loading?: boolean;
  onChangeAddress?: (address?: string, addressOption?: any) => void;
  onChangeDates?: (address?: IHeaderSearchFilterDates) => void;
  onChangeGuests?: (guests?: IHeaderSearchFilterGuests) => void;
  onConfirmAddress?: (address?: any, skipSubmit?: boolean) => void;
  onConfirmDates?: (dates?: IHeaderSearchFilterDates) => void;
  onConfirmGuests?: (guests: IHeaderSearchFilterGuests) => void;
  popularDestinations?: ILocationSearchAutocompleteListOption[];
  alertStatus?: boolean;
  isPromoSearch?: boolean;
  isGuestOccupancyAvailable?: boolean;
  isSERP?: boolean;
  scrollToSearchFilter?: () => void;
  variant?: 'minimal' | 'enhanced';
  disabledDays?: DayPickerProps['disabled'];
  useModeSwitcher?: boolean;
}

const DesktopNavbarSearch: React.FC<IDesktopNavbarSearchProps> = ({
  onSubmit,
  location,
  address,
  dates,
  guests,
  recentSearches = [],
  maxRecentSearches,
  loading = false,
  onChangeAddress,
  onChangeDates,
  onChangeGuests,
  onConfirmAddress,
  onConfirmDates,
  onConfirmGuests,
  popularDestinations = [],
  alertStatus,
  handleRecentSearchNavigation,
  isPromoSearch,
  variant,
  isGuestOccupancyAvailable,
  isSERP,
  scrollToSearchFilter,
  disabledDays,
  useModeSwitcher,
}) => {
  const dispatch = useDispatch();
  const { isMobile } = useBreakpoint();
  const router = useRouter();
  const searchResult = useSelector(getSearchResults);
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );
  const isRvSearchPage = router.pathname === '/rv-search';
  const { isLocalityPage } = useIsLocalityPage();
  const isEscapesSearchPage = router?.pathname?.includes(PAGE_PATHNAME[EPage.ESCAPES_SEARCH]);

  const isEnhanced = variant === 'enhanced';
  const isMinimal = variant === 'minimal';
  const hasVariant = isEnhanced || isMinimal;

  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(true);
  const [selectedTab, setSelectedTab] = useState<SEARCH_TAB | null>(null);
  const [addressValue, setAddressValue] = useState(address || '');
  const modeSwitcherRef = useRef<HTMLDivElement>(null);

  const queryParams = useQueryParams();
  const rentalType = queryParams?.[ESearchFilters.RENTAL_CATEGORY] || ERentalCategory.RV;

  const suggestions = useLocationSearchSuggestions(
    addressValue,
    location ? [location.lng, location.lat] : undefined,
  );

  const formattedDates = useMemo<string | null>(() => {
    if (!dates?.from || !dates?.to) {
      return null;
    }

    return formatDateFullRange(dates.from.toString(), dates.to.toString());
  }, [dates]);

  useEffect(() => {
    if (address !== addressValue) {
      setAddressValue(address || '');
    }
  }, [address, addressValue]);

  const { options, optionGroupLabels } = useLocationSearchAutocompleteOptions({
    shouldBuildGroupedOptions: false,
    suggestions,
    recentSearches,
    popularDestinations,
    includeRecentSearches: !isEscapesSearchPage,
  });

  const handleAddressChange = (
    e: React.ChangeEvent<Record<string, string>>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (!e) return;
    if (selectedTab !== SEARCH_TAB.LOCATION) {
      // Case where text is selected and the user swipes outside the input field.
      handleClickTab(SEARCH_TAB.LOCATION);
    }

    if (reason === 'input') {
      onChangeAddress?.(value);
      setAddressValue(value);
    }
    setSurfacedLocation(LocationGroupType.OTHER);
  };

  const handleAddressSelect = (_e: React.ChangeEvent<Record<string, string>>, option: any) => {
    if (!option) return;
    const { label, url, groupName, value } = option;

    if (url) {
      handleRecentSearchNavigation?.(url);
      return;
    }

    if (value?.name) {
      onChangeAddress?.(value.name, option);
      setAddressValue(value.name);
    } else if (value?.address) {
      onChangeAddress?.(value.address, option);
      setAddressValue(value.address);
    } else {
      onChangeAddress?.(label, option);
      setAddressValue(label);
    }

    const groupType = mapLocationLabelToGroupName(groupName);
    setSurfacedLocation(groupType);
    if (!dates && label) {
      setSelectedTab(SEARCH_TAB.DATES);
      onConfirmAddress?.(option, true);
    }
    if (dates && label) {
      setSelectedTab(null);
      onConfirmAddress?.(option);
    }
  };

  const handleChangeDate = (value?: IHeaderSearchFilterDates) => {
    if ((value?.from && value?.to) || isEmpty(value)) {
      onChangeDates?.(value);

      if (isMinimal) {
        setIsComponentVisible(false);
      } else if (value?.from && value?.to) {
        // When both dates are selected, automatically move focus to guests tab
        handleClickTab(SEARCH_TAB.DETAILS);
      }
    }
  };

  const handleOpen = () => {
    setSelectedTab(SEARCH_TAB.LOCATION);
  };

  const { getInputProps, getListboxProps, getOptionProps, groupedOptions, focused } =
    useAutocomplete({
      inputValue: addressValue,
      options,
      filterOptions: (autocompleteOptions, autocompleteState) => {
        const input = stripDiacritics(autocompleteState.inputValue.trim().toLowerCase());

        if (input.length <= 2) {
          return autocompleteOptions;
        }

        return autocompleteOptions.filter(autocompleteOption => {
          // Filter everything except suggestions
          if (autocompleteOption.groupName === optionGroupLabels.suggestions) {
            return autocompleteOption;
          }

          const candidate = stripDiacritics(
            autocompleteState.getOptionLabel(autocompleteOption).trim().toLowerCase(),
          );

          return candidate.indexOf(input) > -1;
        });
      },
      getOptionLabel: option => option.label,
      groupBy: option => option.groupName,
      onInputChange: handleAddressChange,
      onChange: handleAddressSelect,
      onOpen: handleOpen,
      freeSolo: true,
      open: selectedTab === SEARCH_TAB.LOCATION,
    });

  const handleClickTab = useMemo(() => {
    return (tab: SEARCH_TAB) => {
      setIsComponentVisible(true);
      setSelectedTab(currentSelectedTab => {
        return tab !== SEARCH_TAB.LOCATION && tab === currentSelectedTab ? null : tab;
      });
    };
  }, [setIsComponentVisible]);

  useEffect(() => {
    if (focused) {
      handleClickTab(SEARCH_TAB.LOCATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused]);

  const datesTabIsOpened = isComponentVisible && selectedTab === SEARCH_TAB.DATES;

  // Allow to open dates tab from outside
  // Because we close the tab on click outside, we have to delay the opening
  useEffect(() => {
    if (isMobile) return;

    const handleOnOpenSearchDates = () => {
      if (datesTabIsOpened) return;
      setTimeout(() => handleClickTab(SEARCH_TAB.DATES), 100);
    };
    document.addEventListener(EVENT_OPEN_SEARCH_DATES, handleOnOpenSearchDates);
    return () => document.removeEventListener(EVENT_OPEN_SEARCH_DATES, handleOnOpenSearchDates);
  }, [isMobile, datesTabIsOpened, handleClickTab]);

  // action when closing dropdown
  useEffect(() => {
    if (!isComponentVisible) {
      if (selectedTab === SEARCH_TAB.DETAILS && guests) {
        onConfirmGuests?.(guests);
      }
      if (selectedTab === SEARCH_TAB.DATES && dates) {
        onConfirmDates?.(dates);
      }

      setSelectedTab(null);
    }
  }, [isComponentVisible, guests, onConfirmGuests, onConfirmDates, dates, selectedTab]);

  // we may hide some recent searches but require all options to calculate the index into the
  // results for getting option props (onClick handler)
  const allGroupOptions = groupedOptions as unknown as IGroupOption[];
  const groupOptions = limitRecentSearches(
    groupedOptions as unknown as IGroupOption[],
    optionGroupLabels.recentSearches,
    maxRecentSearches,
  );

  const handleSubmit = (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    if (
      !address &&
      !hasVariant &&
      (!isPromoSearch || (isPromoSearch && !dates?.from)) &&
      dates?.from &&
      dates?.to &&
      searchResult.meta
    ) {
      const { lat, lng, city, state, country_name } = searchResult.meta;

      const searcherGeoLocation = [city, state, country_name].filter(Boolean).join(', ');

      if (searcherGeoLocation) {
        onChangeAddress?.(searcherGeoLocation, null);
        handleChangeDate?.(dates);
        setAddressValue(searcherGeoLocation);
        setSelectedTab(null);
        dispatch(
          applySearchFilter(
            {
              [ESearchFilters.ADDRESS]: searcherGeoLocation,
              [ESearchFilters.DELIVERY_DETAILS]: encodeURIComponent(
                JSON.stringify({
                  country: country_name,
                  state,
                  city,
                }),
              ),
              [ESearchFilters.DELIVERY_ADDRESS]: searcherGeoLocation,
              [ESearchFilters.DELIVERY_QUERY]: searcherGeoLocation,
              [ESearchFilters.DELIVERY_CENTER]: JSON.stringify([lng, lat]),
              [ESearchFilters.DATE_FROM]: dates?.from
                ? dayjs(dates.from).format(QUERYSTRING_DATE_FORMAT)
                : undefined,
              [ESearchFilters.DATE_TO]: dates?.to
                ? dayjs(dates.to).format(QUERYSTRING_DATE_FORMAT)
                : undefined,
              [ESearchFilters.FLEXIBLE_DAYS]: dates?.flexible_days
                ? String(dates.flexible_days)
                : undefined,
            },
            false,
            false,
            false,
            undefined,
            false,
          ),
        );
      }
    } else if (!address && !hasVariant && (!isPromoSearch || (isPromoSearch && !dates?.from))) {
      const inputEl = getInputProps() as any;
      inputEl.ref.current.focus();
      inputEl.onFocus();
    } else if (hasVariant && !dates?.from) {
      handleClickTab(SEARCH_TAB.DATES);
    } else {
      onSubmit?.();
      setSelectedTab(null);
      if (isEnhanced) {
        scrollToSearchFilter?.();
      }
    }
  };

  const dateTabText = formattedDates
    ? `${formattedDates} ${dates?.flexible_days ? ` (±${dates?.flexible_days})` : ''}`
    : '';

  const shouldOpenContentTab =
    selectedTab === SEARCH_TAB.DATES ||
    selectedTab === SEARCH_TAB.DETAILS ||
    selectedTab === SEARCH_TAB.MODE ||
    (selectedTab === SEARCH_TAB.LOCATION && groupOptions.length > 0);

  // Hide popover when location, guests, or dates tabs are open (but not mode tab)
  // Also hide when login, signup, or mobile menu modal is open
  const isAuthModalOpen = useAuthModalState();
  const shouldHidePopoverTemporarily =
    selectedTab === SEARCH_TAB.LOCATION ||
    selectedTab === SEARCH_TAB.DATES ||
    selectedTab === SEARCH_TAB.DETAILS ||
    selectedTab === SEARCH_TAB.MODE ||
    isAuthModalOpen;

  return (
    <>
      <form
        className={clsx({
          ['absolute top-3']: !isPromoSearch && !hasVariant && !isSERP,
        })}
        onSubmit={handleSubmit}
        ref={ref}>
        <div
          className={clsx(
            'bg-white flex items-center border border-solid border-neutral-20 w-fit',
            isEnhanced
              ? 'rounded-full pl-3 h-[100px] flex items-center justify-center'
              : 'rounded-full h-[48px]',
            {
              'border-none': isSERP || isLocalityPage,
            },
          )}>
          {useModeSwitcher && (
            <div ref={modeSwitcherRef} className="flex justify-center h-full">
              <ModeSwitcher onClick={() => handleClickTab(SEARCH_TAB.MODE)} variant={variant} />
            </div>
          )}
          {!hasVariant && (
            <LocationSearchInput getInputProps={getInputProps} alertStatus={alertStatus} />
          )}
          <DateTab
            onClick={() => handleClickTab(SEARCH_TAB.DATES)}
            selectedDates={dateTabText}
            variant={variant}
          />
          <GuestsTab
            onClick={() => handleClickTab(SEARCH_TAB.DETAILS)}
            guests={guests}
            variant={variant}
            isGuestOccupancyAvailable={isGuestOccupancyAvailable}
          />
          <SearchButton loading={loading} variant={variant} />
        </div>

        {shouldOpenContentTab && (
          <div
            data-tab="dropdown_wrapper"
            className={clsx('relative', {
              ['z-10 max-w-30']: isPromoSearch || hasVariant,
            })}>
            <div
              data-tab={selectedTab}
              className={clsx(
                'absolute top-0 overflow-hidden bg-white mt-2 rounded-2xl shadow-100',
                {
                  'w-fit': rentalType === ERentalCategory.RV,
                  'ml-[166px]':
                    selectedTab === SEARCH_TAB.DETAILS &&
                    !hasVariant &&
                    rentalType === ERentalCategory.RV,
                  'ml-[60px]':
                    selectedTab === SEARCH_TAB.LOCATION &&
                    isRentalsVDeliverySearchModeEnabled &&
                    isRvSearchPage,
                  'p-4': selectedTab !== SEARCH_TAB.MODE,
                  'p-6 w-[25rem]': selectedTab === SEARCH_TAB.MODE,
                  'right-0': isMinimal,
                },
              )}>
              <div className="overflow-auto max-h-[calc(100vh-7.5em)]">
                {!hasVariant && (
                  <LocationSearchContent
                    open={selectedTab === SEARCH_TAB.LOCATION}
                    groupOptions={groupOptions}
                    allGroupOptions={allGroupOptions}
                    getListboxProps={getListboxProps}
                    getOptionProps={getOptionProps}
                  />
                )}
                <DateContent
                  dates={dates}
                  onChangeDates={handleChangeDate}
                  open={selectedTab === SEARCH_TAB.DATES}
                  disabledDays={disabledDays}
                />
                {hasVariant || isGuestOccupancyAvailable ? (
                  <DetailsContent
                    guests={guests}
                    onChangeGuests={onChangeGuests}
                    onConfirmGuests={onConfirmGuests}
                    open={selectedTab === SEARCH_TAB.DETAILS}
                    isGuestOccupancyAvailable={isGuestOccupancyAvailable}
                  />
                ) : (
                  <GuestsContent
                    guests={guests}
                    onChangeGuests={onChangeGuests}
                    open={selectedTab === SEARCH_TAB.DETAILS}
                  />
                )}
                {selectedTab === SEARCH_TAB.MODE && (
                  <ModeSwitcherContent
                    open={selectedTab === SEARCH_TAB.MODE}
                    onModeSelect={() => setSelectedTab(null)}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </form>

      {useModeSwitcher && (
        <SearchModesPopover
          containerRef={modeSwitcherRef}
          shouldHideTemporarily={shouldHidePopoverTemporarily}
        />
      )}
    </>
  );
};

export default DesktopNavbarSearch;
